import { Typography } from '@cannect/new-components/atoms'
import { useMarkTicketRead, useMyTicket, useMyTickets } from '@cannect/services/resources/supportChat'
import { useEffect } from 'react'
import { TSupportTicket, TSupportTicketStatus } from '@cannect/services/resources/supportChat/types'
import { useHistory, useLocation } from 'react-router'
import { FiArrowLeft, FiClock, FiInbox } from 'react-icons/fi'

/**
 * Minhas solicitações.
 *
 * O que o paciente acompanha quando o caso dele não se resolveu no chat — a
 * tratativa com a transportadora, por exemplo. A conversa termina; a
 * solicitação continua, e é aqui que ele vê o andamento.
 *
 * Só aparece o que o analista marcou como visível: a tratativa interna fica
 * no admin.
 */

const STATUS_TONE: Record<TSupportTicketStatus, string> = {
  open: 'bg-neutral-100 text-neutral-700',
  in_progress: 'bg-blue-50 text-blue-700',
  waiting_third_party: 'bg-amber-50 text-amber-700',
  resolved: 'bg-green-50 text-green-700',
  closed: 'bg-neutral-100 text-neutral-500'
}

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleDateString('pt-BR') : null)

/*
  Previsão só aparece quando existe. "Sem previsão ainda" é melhor que uma
  data inventada — e é por isso que o analista não é obrigado a dar uma.
*/
const ResolutionLine = ({ ticket }: { ticket: TSupportTicket }) => {
  if (ticket.status === 'closed') {
    return ticket.closed_at ? (
      <span className="text-xs text-neutral-500">Encerrada em {formatDate(ticket.closed_at)}</span>
    ) : null
  }

  return (
    <span className="flex items-center gap-1 text-xs text-neutral-500">
      <FiClock size={12} />
      {ticket.expected_resolution_at ? `Previsão: ${formatDate(ticket.expected_resolution_at)}` : 'Sem previsão ainda'}
    </span>
  )
}

const TicketCard = ({ ticket, onOpen }: { ticket: TSupportTicket; onOpen: () => void }) => (
  <button
    type="button"
    onClick={onOpen}
    className="flex w-full flex-col gap-1.5 rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-left transition hover:border-primary-300">
    <div className="flex items-start justify-between gap-3">
      <span className="flex items-center gap-2 font-medium text-neutral-900">
        {ticket.title}
        {(ticket.unread_count ?? 0) > 0 && (
          <span className="flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-green-700">
            <span className="h-1.5 w-1.5 rounded-full bg-green-600" />
            Atualizada
          </span>
        )}
      </span>
      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_TONE[ticket.status]}`}>
        {ticket.status_label}
      </span>
    </div>

    <span className="text-xs text-neutral-500">
      Aberta em {formatDate(ticket.opened_at)}
      {ticket.order_id ? ` · Pedido #${ticket.order_id}` : ''}
    </span>

    <ResolutionLine ticket={ticket} />
  </button>
)

const MySupportTickets = () => {
  const history = useHistory()
  const { pathname, search } = useLocation()

  /*
    A solicitação aberta vive na URL (`?solicitacao=<uuid>`), como o `?ajuda=`
    da lista de pedidos: é o que deixa o botão do e-mail abrir direto nela, e
    recarregar a página não perde a tela. Quem confere o dono é a API — um
    uuid de outra pessoa volta 404 e cai em "não encontrada".
  */
  const selected = new URLSearchParams(search).get('solicitacao')

  const setSelected = (uuid: string | null) => {
    const params = new URLSearchParams(search)

    if (uuid) params.set('solicitacao', uuid)
    else params.delete('solicitacao')

    history.replace(`${pathname}?${params.toString()}`)
  }

  const { data, isLoading } = useMyTickets()
  const detail = useMyTicket(selected)
  const { mutate: markRead } = useMarkTicketRead()

  // Abrir a solicitação é ler: a etiqueta "Atualizada" some da lista.
  useEffect(() => {
    if (selected && detail.data) markRead(selected)
  }, [selected, detail.data, markRead])

  if (selected) {
    return (
      <div className="flex flex-col gap-4">
        <button
          type="button"
          onClick={() => setSelected(null)}
          className="flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900">
          <FiArrowLeft size={14} />
          Voltar para as solicitações
        </button>

        {detail.isLoading && <p className="text-sm text-neutral-500">Carregando…</p>}

        {/* Link antigo ou de outra conta: a API devolve 404. */}
        {detail.isError && (
          <p className="rounded-xl bg-neutral-100 px-4 py-3 text-sm text-neutral-600">
            Não encontramos esta solicitação na sua conta.
          </p>
        )}

        {detail.data && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <Typography.Text type="paragraphOne" weight="semibold">
                {detail.data.title}
              </Typography.Text>
              <span className="text-xs text-neutral-500">
                Aberta em {formatDate(detail.data.opened_at)}
                {detail.data.order_id ? ` · Pedido #${detail.data.order_id}` : ''}
              </span>
              <span
                className={`w-fit rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                  STATUS_TONE[detail.data.status]
                }`}>
                {detail.data.status_label}
              </span>
              <ResolutionLine ticket={detail.data} />
            </div>

            {/* Sem atualização visível ainda: a lista vazia parecia tela quebrada. */}
            {detail.data.updates.length === 0 ? (
              <p className="rounded-xl bg-neutral-100 px-4 py-3 text-sm text-neutral-600">
                Recebemos sua solicitação. As atualizações vão aparecer aqui.
              </p>
            ) : (
              <ol className="flex flex-col gap-3 border-l border-neutral-200 pl-4">
                {detail.data.updates.map((update) => (
                  <li key={update.id} className="relative">
                    <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-primary-500" />
                    <p className="text-sm text-neutral-800">{update.body}</p>
                    <span className="text-xs text-neutral-500">
                      {new Date(update.created_at).toLocaleString('pt-BR')}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </div>
    )
  }

  const abertas = data?.open ?? []
  const fechadas = data?.closed ?? []

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <Typography.Text type="paragraphOne" weight="semibold">
          Minhas solicitações
        </Typography.Text>
        <Typography.Text type="captionOne" muted>
          Acompanhe aqui os casos que a nossa equipe está tratando para você.
        </Typography.Text>
      </div>

      {isLoading && <p className="text-sm text-neutral-500">Carregando…</p>}

      {!isLoading && abertas.length === 0 && fechadas.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-neutral-300 px-4 py-10 text-center">
          <FiInbox className="text-neutral-400" size={24} />
          <Typography.Text type="captionOne" muted>
            Você não tem solicitações em andamento.
          </Typography.Text>
        </div>
      )}

      {abertas.length > 0 && (
        <section className="flex flex-col gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">Em andamento</span>
          {abertas.map((ticket) => (
            <TicketCard key={ticket.uuid} ticket={ticket} onOpen={() => setSelected(ticket.uuid)} />
          ))}
        </section>
      )}

      {fechadas.length > 0 && (
        <section className="flex flex-col gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">Encerradas</span>
          {fechadas.map((ticket) => (
            <TicketCard key={ticket.uuid} ticket={ticket} onOpen={() => setSelected(ticket.uuid)} />
          ))}
        </section>
      )}
    </div>
  )
}

export default MySupportTickets
